import "./avatarUploadModal.css";
import { Upload, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { icons } from "../../utils/globalImages";
import { toggleReFetch } from "../../redux/slices/themeSlice";
import BackdropLoader from "../BackdropLoader/BackdropLoader";
import { getAvatar } from "../../api/modules/b2droppie.class";
import { updateUserAvatar } from "../../redux/slices/authSlice";
import useLockBodyScroll from "../../redux/customHooks/useLockBodyScroll";

const AvatarUploadModal = ({ open, onClose, currentAvatar, isDarkMode }) => {
  const dialogRef = useRef(null);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);

  useEffect(() => {
    if (!open) return;
    fetchAvatars();
  }, [open]);

  useLockBodyScroll(open);

  const fetchAvatars = async () => {
    setLoading(true);
    try {
      const response = await getAvatar();
      setAvatars(response?.data?.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);

    setUploadedImage(imageUrl);
    setUploadedFile(file);
    setSelectedAvatar(null);
  };

  const handleSaveAvatar = async () => {
    try {
      let formData = new FormData();

      if (uploadedFile) {
        formData.append("avatar", uploadedFile);
      } else if (selectedAvatar) {
        formData.append("avatar", selectedAvatar);
      } else {
        console.log("No avatar selected");
        return;
      }

      setLoading(true);

      const res = await dispatch(updateUserAvatar(formData));

      if (res) {
        dispatch(toggleReFetch("mine"));
      }

      console.log("Avatar updated:", res);

      handleClose();
    } catch (err) {
      console.error("Avatar update failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const resetState = () => {
    setAvatars([]);
    setLoading(false);
    setUploadedFile(null);
    setUploadedImage(null);
    setSelectedAvatar(null);

    // reset file input value (important)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <div
        className={`avatar_dialog_backdrop ${isDarkMode ? "dark" : "light"}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div ref={dialogRef} className="avatar_dialog">
          <div className="avatar_dialog_content">
            {/* Upload */}
            <div className="avatar_upload_column">
              <h3 className="text-18px text-fw-400 text-AktivGrotesk-Bold text-white mb-0">
                Change Avatar
              </h3>
              <div className="avatar_upload_box d-center">
                <div className="avatar_upload_preview_desktop">
                  {uploadedImage ? (
                    <img src={uploadedImage} alt="Preview" />
                  ) : selectedAvatar ? (
                    <img src={selectedAvatar} alt="Avatar" />
                  ) : currentAvatar ? (
                    <img src={currentAvatar} alt="Avatar" />
                  ) : null}
                </div>

                <div className="d-block">
                  <button
                    onClick={handleBrowseClick}
                    className="filled-charcoal-btn-dark d-center h-32px text-14px text-fw-400 gap-8px"
                  >
                    <img src={icons.uploadFilled} alt="upload" />
                    Upload Photo
                  </button>

                  <span className="text-light-gray text-14px text-fw-400 mt-8px">
                    Browse files or drag and drop here
                  </span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            </div>

            {/* Avatar list */}
            <div className="avatar-select-column mt-0">
              <span className="text-18px text-fw-400 text-AktivGrotesk-Bold">
                Select Avatar
              </span>
              <div className="row mt-24px">
                {avatars.map((avatar, i) => (
                  <div
                    key={i}
                    className={`col-2 mt-2 cursor-pointer`}
                    onClick={() => {
                      setSelectedAvatar(avatar.url);
                      setUploadedImage(null);
                      setUploadedFile(null);
                    }}
                  >
                    <div
                      className={`avatar-img-wrapper ${
                        selectedAvatar === avatar.url ? "selected" : ""
                      }`}
                    >
                      <img
                        className="avatar-option"
                        src={avatar.url}
                        alt="avatar"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="d-end avatar-bottom-row">
            <div className="d-start gap-10px p-3">
              <button
                onClick={handleClose}
                className="deep-primary-monochrome-btn-dark h-48px w-93px"
              >
                Cancel
              </button>

              <button
                onClick={handleSaveAvatar}
                className="filled-medium-deep-yellow-btn-dark h-48px w-78px"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      <BackdropLoader show={loading} />
    </>
  );
};

export default AvatarUploadModal;
